import React, { useEffect, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Box from "@material-ui/core/Box";
import { format } from "date-fns";
import parseISO from "date-fns/parseISO";
import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import Textarea from "components/Textarea";
import { useClickBlockerContext } from "components/BlockableClickContext";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import useStyles from "./styles";

export interface Props {
  comment?: racehorse360.IWorkoutRequestComment;
  onSave: (commentId: string, textComment: string) => void;
  onCancel: () => void;
  className?: string;
  commentDateText?: string;
  unsavedChangesPrompt?: React.ReactElement;
}

const HorseWorkoutCommentForm = (props: Props) => {
  const { onCancel, onSave, comment, commentDateText, unsavedChangesPrompt } =
    props;
  const classes = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("xs"));
  const { setBlockClick, setBlockClickCallback } = useClickBlockerContext();
  const [commentText, setCommentText] = useState(comment?.body || "");
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);

  const lockNavigation = () => {
    setBlockClick(true);
    setBlockClickCallback(() => () => setUnsavedChangesDialogOpen(true));
  };

  const unlockNavigation = () => {
    setBlockClick(false);
    setBlockClickCallback(null);
  };

  useEffect(() => {
    return () => {
      unlockNavigation();
    };
  }, []);

  const handleTextChange = text => {
    setCommentText(text);
    if (comment?.body !== text) {
      lockNavigation();
    } else {
      unlockNavigation();
    }
  };

  const handleCancelWithoutConfirm = () => {
    unlockNavigation();
    onCancel();
  };

  const handleCancelUnsaved = () => {
    setUnsavedChangesDialogOpen(false);
  };

  const handleDiscardUnsaved = () => {
    setUnsavedChangesDialogOpen(false);
    unlockNavigation();
    onCancel();
  };

  const handleSave = () => {
    setUnsavedChangesDialogOpen(false);
    unlockNavigation();
    setCommentText("");
    onSave(comment?.id, commentText.trim());
    onCancel();
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.formBox}>
        <Textarea
          showControls
          controlsClasses={{
            root: classes.actions,
            button: classes.button,
            save: classes.save
          }}
          autoFocus
          maxLength={500}
          rows={matches ? 9 : 6}
          defaultValue={comment?.body || ""}
          label={
            commentDateText ||
            format(
              comment?.id ? parseISO(comment?.updatedOn) : new Date(),
              "iiii, MMMM dd - h:mm a"
            )
          }
          onChange={handleTextChange}
          onSave={handleSave}
          onCancel={handleCancelWithoutConfirm}
        />
      </Box>
      <UnsavedChangesDialog
        onSave={handleSave}
        onDiscard={handleDiscardUnsaved}
        onCancel={handleCancelUnsaved}
        open={unsavedChangesDialogOpen}
        content={unsavedChangesPrompt}
      />
    </Box>
  );
};

export default HorseWorkoutCommentForm;
